import { useState, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';

const CLUBS_DATA_URL = '/data/clubes.json';
const CACHE_KEY = 'vp_clubs_data';
const CACHE_VERSION_KEY = 'vp_clubs_version';
const CURRENT_VERSION = '1.0.1'; // Incrementado para forzar recarga de nuevos datos de clubes

/**
 * Hook para gestionar la carga y búsqueda de clubes de pádel.
 * Utiliza caché local (localStorage) para evitar descargas repetidas.
 */
import { getClubs } from '../services/api';

/**
 * Hook para gestionar la carga y búsqueda de clubes de pádel.
 * Utiliza caché local (localStorage) para evitar descargas repetidas.
 * @param {string} organizerId - (Opcional) Si se proporciona, carga solo los clubes de este organizador desde Firebase.
 */
export const useClubs = (organizerId = null) => {
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadClubs = async () => {
            setLoading(true);
            try {
                if (organizerId !== undefined && organizerId !== null) {
                    // Cargar clubes del organizador desde Firebase
                    // If organizerId is empty string, we just want empty results
                    if (!organizerId) {
                        setClubs([]);
                        setLoading(false);
                        return;
                    }

                    const orgClubs = await getClubs({ organizerId });
                    
                    // Normalizar formato para que coincida con el del JSON (n: nombre, d: dirección)
                    // Esto es necesario para que Fuse.js y ClubSearch funcionen igual
                    const normalizedClubs = orgClubs.map(c => ({
                        ...c,
                        n: c.name,
                        d: c.address ? `${c.address}, ${c.city || ''}` : (c.city || ''),
                        // Mantener campos originales por si acaso
                    }));
                    
                    setClubs(normalizedClubs);
                    setLoading(false);
                } else {
                    // Comportamiento original: Cargar JSON público con caché
                    // 1. Intentar cargar desde caché
                    const cachedData = localStorage.getItem(CACHE_KEY);
                    const cachedVersion = localStorage.getItem(CACHE_VERSION_KEY);

                    if (cachedData && cachedVersion === CURRENT_VERSION) {
                        setClubs(JSON.parse(cachedData));
                        setLoading(false);
                        // Opcional: Validar en segundo plano si hay actualizaciones
                    }

                    // 2. Cargar desde el servidor (si no hay caché o para actualizar)
                    const response = await fetch(CLUBS_DATA_URL);
                    if (!response.ok) throw new Error('No se pudo cargar la base de datos de clubes');
                    
                    const data = await response.json();
                    
                    // 3. Actualizar estado y caché
                    setClubs(data);
                    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
                    localStorage.setItem(CACHE_VERSION_KEY, CURRENT_VERSION);
                    setLoading(false);
                }
            } catch (err) {
                console.error('Error cargando clubes:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        loadClubs();
    }, [organizerId]);

    // Configuración de Fuse.js para búsqueda difusa
    const fuse = useMemo(() => {
        if (clubs.length === 0) return null;
        return new Fuse(clubs, {
            keys: [
                { name: 'n', weight: 1.0 }, // Nombre del club (prioridad máxima)
                { name: 'd', weight: 0.3 }  // Dirección
            ],
            threshold: 0.2,   // Mucho más estricto para evitar falsos positivos
            distance: 1000,   // Buscar en toda la cadena
            ignoreLocation: true,
            minMatchCharLength: 2,
            useExtendedSearch: true
        });
    }, [clubs]);

    /**
     * Función de búsqueda
     * @param {string} query - Texto a buscar
     * @returns {Array} - Resultados de la búsqueda
     */
    const searchClubs = (query) => {
        if (!query || query.trim() === '') {
            return organizerId ? clubs : [];
        }
        if (query.length < 2 && !organizerId) return [];
        if (!fuse) return [];
        
        const trimmedQuery = query.trim();
        const isZipCode = /^\d{5}$/.test(trimmedQuery);
        
        let results;
        if (isZipCode) {
            // Para CP, usamos búsqueda exacta dentro de la dirección
            results = fuse.search(`'${trimmedQuery}`); // El prefijo ' significa match exacto en Fuse
        } else {
            results = fuse.search(trimmedQuery);
        }
        
        const rawResults = results.map(result => result.item);
        
        // Eliminar duplicados exactos (basado en nombre y dirección)
        const uniqueResults = [];
        const seen = new Set();
        
        for (const club of rawResults) {
            const key = `${club.n}|${club.d}`;
            if (!seen.has(key)) {
                seen.add(key);
                uniqueResults.push(club);
            }
        }
        
        return uniqueResults;
    };

    return {
        clubs,
        loading,
        error,
        searchClubs
    };
};
